import { useCallback, useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { useClerk } from "@clerk/react";
import { api } from "../lib/convexApi";
import { AuthShell } from "./AuthShell";
import { parseGetParams } from "../funnel/getParams";

import { CheckoutLaunch } from "./CheckoutLaunch";
import { PLANS } from "./PricingPage";

export function GuestCheckout() {
  const { plan, period } = parseGetParams(window.location.search);
  const createCheckout = useAction(api.billing.checkout.createGuestCheckout);
  const busy = useRef(false);
  const [error, setError] = useState(false);
  const openCheckout = useCallback(async () => {
    if (!plan || busy.current) return;
    busy.current = true;
    setError(false);
    try {
      // Reuse across reloads and StrictMode mounts. Storage-disabled browsers
      // still open checkout; the server never relies on this key for access.
      let requestId: string;
      try {
        const key = `steerly-checkout:${plan}:${period}`;
        requestId = sessionStorage.getItem(key) || crypto.randomUUID();
        sessionStorage.setItem(key, requestId);
      } catch {
        requestId = crypto.randomUUID();
      }
      const result = await createCheckout({ tier: plan, period, requestId });
      // Replace the temporary /get entry: Back returns to the website,
      // rather than mounting /get and immediately redirecting again.
      window.location.replace(result.url);
    } catch {
      busy.current = false;
      setError(true);
    }
  }, [createCheckout, plan, period]);
  useEffect(() => {
    void openCheckout();
  }, [openCheckout]);
  const selectedPlan = PLANS.find((p) => p.tier === plan)!;
  return (
    <CheckoutLaunch
      planName={selectedPlan.name}
      periodLabel={period === "yearly" ? "Annual" : "Monthly"}
      priceLabel={period === "yearly" ? selectedPlan.annual : selectedPlan.monthly}
      phase={error ? "error" : "opening"}
      onOpenCheckout={() => void openCheckout()}
      onSeeAllPlans={() => window.location.assign("https://steerly.ai/pricing.html")}
      accountEmail={null}
      onSignOut={() => {}}
    />
  );
}

const ACTIVATION_COPY: Record<string, string> = {
  checking: "Confirming your purchase…",
  pending:
    "Your purchase isn't available yet. Payment confirmation can take a moment. Make sure this account has the same verified email you used at checkout, then retry.",
  "verify-email": "Verify the email you used at checkout in your account, then retry.",
  "contact-support":
    "We found more than one subscription to reconcile. Contact support before purchasing again.",
  error:
    "We couldn't activate your plan yet. Please retry or contact support. You don't need to purchase again.",
};

export function ActivatePurchase() {
  const purchaseRef = new URLSearchParams(window.location.search).get("purchase") || undefined;
  const activationUrl = purchaseRef
    ? `/activate?purchase=${encodeURIComponent(purchaseRef)}`
    : "/activate";
  const claim = useAction(api.billing.checkout.claimGuestPurchase);
  const clerk = useClerk();
  const [status, setStatus] = useState("checking");
  const busy = useRef(false);
  const attempt = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setStatus("checking");
    try {
      const result = await claim(purchaseRef ? { purchaseRef } : {});
      if (result.status === "activated") {
        // Guest purchases are personal seats, independent of any remembered
        // organization from a previous login.
        await clerk.setActive({ organization: null });
        window.location.replace("/download?activated=1");
        return;
      }
      setStatus(result.status);
    } catch {
      setStatus("error");
    } finally {
      busy.current = false;
    }
  }, [claim, clerk, purchaseRef]);
  useEffect(() => {
    void attempt();
  }, [attempt]);
  // Bounded retries cover a delayed webhook without an endless polling loop.
  const retries = useRef(0);
  useEffect(() => {
    if (status !== "pending" || retries.current >= 5) return;
    const timer = window.setTimeout(() => {
      retries.current += 1;
      void attempt();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [status, attempt]);
  return (
    <AuthShell>
      <div className="checkout-launch" role="status" aria-live="polite">
        <h1>Activate your Steerly plan</h1>
        <p>Your purchase activates your personal workspace.</p>
        <p>{ACTIVATION_COPY[status] || ACTIVATION_COPY.error}</p>
        {status !== "checking" && (
          <>
            <button type="button" onClick={() => void attempt()}>
              Retry activation
            </button>
            <button type="button" onClick={() => void clerk.openUserProfile()}>
              Manage account email
            </button>
            <button
              type="button"
              onClick={() => void clerk.signOut({ redirectUrl: activationUrl })}
            >
              Use another account
            </button>
            <a href="mailto:support@steerly.ai">Contact support</a>
          </>
        )}
      </div>
    </AuthShell>
  );
}
